import {
  afterNextRender,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTab, MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IIconForm } from '@campaign-form/campaign-form.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ProfileDateFilterStateService } from '@core/services/profile-date-filter-state.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TSocialContent } from '@interfaces/campaign-analytics-api.interface';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignsDatatableComponent } from '@pages/campaigns/campaigns-datatable/campaigns-datatable.component';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignsStore } from '@pages/campaigns/campaigns.store';
import { BaseComponent } from '@shared/components/base.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { FileUploaderService } from '@shared/components/file-uploader/file-uploader.service';
import {
  EDateRangeType,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { CreatorPlatformListComponent } from '../creator-platform-list/creator-platform-list.component';
import { ISetChannelAsDefaultEvent } from '../creator-platform-list/creator-platform-list.interface';
import { IChannel, ICreator } from '../creators.interface';
import { CreatorsStore } from '../creators.store';
import { CreatorDetailsBannerComponent } from './creator-details-banner/creator-details-banner.component';
import { CreatorDetailsDeliverablesComponent } from './creator-details-deliverables/creator-details-deliverables.component';
import { CreatorDetailsFilterService } from './creator-details-filter.service';
import { CreatorDetailsFiltersComponent } from './creator-details-filters/creator-details-filters.component';
import { CreatorDetailsSocialTilesComponent } from './creator-details-social-tiles/creator-details-social-tiles.component';
import { ICreatorDetailsFilter } from './creator-details.interface';
import { CreatorDetailsStore } from './creator-details.store';

@Component({
  imports: [
    RouterModule,
    MatButtonModule,
    HasPermissionDirective,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    CampaignsDatatableComponent,
    CreatorPlatformListComponent,
    ContentContainerComponent,
    MatTabsModule,
    CreatorDetailsBannerComponent,
    CreatorDetailsDeliverablesComponent,
    CreatorDetailsFiltersComponent,
    CreatorDetailsSocialTilesComponent,
  ],
  providers: [CreatorsStore, CampaignsStore, CreatorDetailsStore],
  selector: 'app-creator-details',
  templateUrl: './creator-details.component.html',
})
export class CreatorDetailsComponent extends BaseComponent implements OnInit {
  public readonly adminMode = input<boolean>(false);
  public readonly creatorMode = input<boolean>(false);

  public readonly creatorCreated = output<ICreator>();
  public readonly creatorUpdated = output<ICreator>();

  public readonly creator = signal<ICreator | null>(null);
  public readonly dateFilterState = signal<IProfileDateFilterState | null>(null);
  public readonly campaigns = signal<TCampaignApi[]>([]);
  public readonly socialContents = signal<TSocialContent[]>([]);
  public readonly socialContentsOriginal = signal<TSocialContent[]>([]);
  public readonly isCreatingCreator = signal<boolean>(false);
  public readonly nameError = signal<string | null>(null);
  public readonly hasUnfilteredCampaigns = signal<boolean>(false);

  public readonly creatorDetailsFilter = signal<ICreatorDetailsFilter>({
    channelTechnicalIds: new Set(),
    endDate: null,
    startDate: null,
  });

  public readonly nameForm: FormGroup<{ name: FormControl<string | null> }> = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
  });

  public readonly iconForm: FormGroup<IIconForm> = new FormGroup({
    icon: new FormControl<string | null>(null),
    imageFile: new FormControl<ICustomFile | null>(null, imageExtensionValidator()),
  });

  public readonly loadedCreatorData = signal(false);
  public readonly loadedCampaignsData = signal(false);
  public readonly hideFilters = signal(true);
  // Mat-tab content without matTabContent is created on page load, so this gates the Deliverables
  // tab's component (and every request it sends) until the tab is first opened. Never reset, so
  // the tab keeps its data and filters when the user switches away and back.
  public readonly deliverablesTabOpened = signal(false);
  public readonly creatorId = signal<string | null>(null);

  public isEditingName = false;
  public channelUrl: string | null = null;

  public readonly campaignsStore = inject(CampaignsStore);
  public readonly store = inject(CreatorDetailsStore);
  private readonly creatorStore = inject(CreatorsStore);
  private readonly profileDateFilterStateService = inject(ProfileDateFilterStateService);
  private readonly route = inject(ActivatedRoute);
  private readonly creatorDetailsFilterService = inject(CreatorDetailsFilterService);
  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private selectedDateRange = EDateRangeType.last30Days;

  private readonly bannerComponent = viewChild(CreatorDetailsBannerComponent);
  private readonly campaignsTab = viewChild<MatTab>('campaignsTab');
  private readonly deliverablesTab = viewChild<MatTab>('deliverablesTab');
  private readonly channelsTab = viewChild<MatTab>('channelsTab');
  private readonly contentsTab = viewChild<MatTab>('contentsTab');
  private dateFilterStateInitialized = false;

  constructor() {
    super();

    this.setCreatorId();
    this.setInitialFilters();
    this.initializeDateFilterStateEffect();
    this.setInitialCreator();
    this.createEffects();
    this.nameFormSubscriptions();
    this.openDeliverablesTabIfInitiallySelected();
  }

  public ngOnInit(): void {
    const creatorId = this.creatorId();
    const adminMode = this.route.snapshot.data['adminMode'] === true;

    if (creatorId) {
      this.store.fetchCreator({ adminMode, creatorId });
    }
  }

  public onBannerNameEditingChanged(isEditing: boolean): void {
    this.isEditingName = isEditing;
    // Clears out a stale error from a previous attempt, whether we're opening the form fresh or
    // the banner just discarded (both go through here) - otherwise a rejected name from an earlier
    // edit would still show red the next time the field opens.
    this.nameError.set(null);
    this.nameForm.get('name')?.setErrors(null);
  }

  public onChannelImageSelected(imageUrl: string): void {
    if (!imageUrl) {
      return;
    }

    this.iconForm.patchValue({ icon: imageUrl });
    this.updateIcon('channel_image');
  }

  public onChannelSelectedAsDefault(event: ISetChannelAsDefaultEvent): void {
    if (!event.iconUrl || !this.creator()) {
      return;
    }

    this.updateName(
      { ...this.creator()!, icon: event.iconUrl },
      this.clearCreatorName(event.creatorName),
      'use_as_default',
    );
    this.iconForm.patchValue({ icon: event.iconUrl }, { emitEvent: false });
    this.eventTrackingService.track(EEventType.creatorImageChanged, {
      creatorId: this.creator()?.id,
      newValue: event.iconUrl ?? undefined,
      previousValue: this.creator()?.icon ?? undefined,
      source: 'use_as_default',
    });
  }

  public onChannelsChanged(channels: IChannel[]): void {
    if (!this.creator()) {
      return;
    }

    this.creator()!.channels = channels;
    this.creatorUpdated.emit(this.creator()!);
  }

  public onSelectedTabChange(event: MatTabChangeEvent): void {
    if (event.tab === this.contentsTab()) {
      setTimeout(() => {
        this.hideFilters.set(false);
      }, 350);
    } else {
      this.hideFilters.set(true);
    }

    if (event.tab === this.deliverablesTab()) {
      this.deliverablesTabOpened.set(true);
    }

    this.trackTabOpened(event.tab);
  }

  public onCreatorDetailsFilterChanged(creatorDetailsFilter: ICreatorDetailsFilter): void {
    this.creatorDetailsFilter.set(creatorDetailsFilter);
    this.eventTrackingService.track(EEventType.creatorFilterChanged, {
      creatorId: this.creatorId() ?? this.creator()?.id,
    });
  }

  public onDateRangeChanged(dateRange: Pick<ICreatorDetailsFilter, 'endDate' | 'startDate'>): void {
    this.creatorDetailsFilter.set({
      ...this.creatorDetailsFilter(),
      endDate: dateRange.endDate,
      startDate: dateRange.startDate,
    });

    this.profileDateFilterStateService.persistState({
      endDate: dateRange.endDate,
      entityId: this.creatorId(),
      entityType: 'creator',
      selectedRange: this.selectedDateRange,
      startDate: dateRange.startDate,
      userId: AuthService.currentDbUser?.id ?? null,
    });

    this.getSocialContents();
  }

  public onSelectedRangeChanged(selectedRange: EDateRangeType): void {
    this.selectedDateRange = selectedRange;
  }

  public onBannerNameSaveRequested(): void {
    if (this.creator() && this.nameForm.valid && this.isEditingName) {
      this.saveCreator();
    }
  }

  public updateIcon(source: 'manual' | 'channel_image' = 'manual'): void {
    const creator = this.creator();

    if (!creator) {
      return;
    }

    const previousValue = creator.icon ?? undefined;
    creator.icon = this.iconForm.value.icon;

    this.creatorStore.updateCreator({
      creator: { icon: this.iconForm.value.icon },
      id: creator.id,
    });

    this.eventTrackingService.track(EEventType.creatorImageChanged, {
      creatorId: creator.id,
      newValue: this.iconForm.value.icon ?? undefined,
      previousValue,
      source,
    });
  }

  private setInitialCreator(): void {
    effect(() => {
      const creator = this.store.creator$();

      if (creator && !untracked(this.creator)) {
        this.setCreator(creator);
      }
    });
  }

  private createEffects(): void {
    this.socialContentsInitialFetchEffect();
    this.socialContentsUpdateEffect();
    this.updatedCreatorEffect();
    this.campaignsEffect();
    this.creatorFetchEffect();
    this.creatorEffect();
    this.loadedCreatorDataEffect();
  }

  private nameFormSubscriptions(): void {
    this.nameForm
      .get('name')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.nameError()) {
          this.nameError.set(null);
          this.nameForm.get('name')?.setErrors(null);
        }
      });
  }

  private creatorFetchEffect(): void {
    effect(() => {
      const canReadCampaigns = this.permissionService.hasPermission(this.EPermissions.campaignReadOwn);

      if (this.adminMode() && this.creatorId()) {
        this.creatorStore.fetchCreator({
          adminMode: true,
          creatorId: this.creatorId()!,
        });

        if (canReadCampaigns) {
          this.fetchCampaigns();
        }

        return;
      }

      if (this.creatorMode()) {
        this.creatorStore.fetchCreator({ creatorMode: true });

        if (canReadCampaigns) {
          this.fetchCampaigns();
        }

        return;
      }

      if (this.creatorId() && canReadCampaigns) {
        this.fetchCampaigns();
      }
    });
  }

  private updatedCreatorEffect(): void {
    effect(() => {
      const updatedCreator = this.creatorStore.updatedCreator$();

      if (!updatedCreator) {
        return;
      }

      if (ImageUtilitiesService.isValidFileId(updatedCreator.icon)) {
        this.setCreator(
          { ...this.creator(), icon: updatedCreator.icon, id: updatedCreator.id, name: updatedCreator.name },
          false,
        );
      }

      this.creatorUpdated.emit({ ...this.creator(), ...updatedCreator, channels: this.creator()?.channels });
      this.creatorStore.updatedCreator$.set(null);
    });
  }

  private creatorEffect(): void {
    effect(() => {
      const creator = this.creatorStore.creator$();

      if (creator && !this.store.creator$() && !untracked(this.creator)) {
        this.setCreator(creator);
        this.creatorStore.creator$.set(null);
        this.store.patchState({ creator, creatorLoading: false });
      }
    });
  }

  private campaignsEffect(): void {
    effect(() => {
      // Reads from the dedicated fetchProfileCampaigns pipeline (see campaigns.store.ts), not
      // campaignsStore.campaigns$() - that field is driven by CampaignsDatatableComponent's own
      // page/sort/filter-triggered fetches for the Campaigns tab's table, and sharing it with this
      // tab's own full-list fetch used to mean whichever response landed last silently overwrote
      // the other's data and cancelled its in-flight request.
      const campaigns = this.campaignsStore.profileCampaigns$();

      if (!this.hasUnfilteredCampaigns() && campaigns?.length > 0) {
        this.hasUnfilteredCampaigns.set(true);
      }

      this.campaigns.set(campaigns);
      this.loadedCampaignsData.set(!this.campaignsStore.profileCampaignsLoading$());
    });
  }

  private loadedCreatorDataEffect(): void {
    effect(() => {
      const creatorLoading = this.store.creatorLoading$();
      const creator = this.store.creator$();

      this.loadedCreatorData.set(!creatorLoading && creator !== null);
    });
  }

  private socialContentsInitialFetchEffect(): void {
    effect(() => {
      if (this.creator()) {
        this.getSocialContents();
      }
    });
  }

  private socialContentsUpdateEffect(): void {
    effect(() => {
      if (this.store.loading$()) {
        return;
      }

      this.socialContents.set(
        this.creatorDetailsFilterService.filterSocialContents(
          this.store.socialContents$() ?? [],
          this.creatorDetailsFilter(),
        ),
      );
      this.socialContentsOriginal.set(structuredClone(this.store.socialContents$() ?? []));
    });
  }

  private fetchCampaigns(): void {
    const creatorId = this.creatorId();
    const creatorMode = this.creatorMode();

    if (creatorId || creatorMode) {
      this.campaignsStore.fetchProfileCampaigns({
        creatorId: creatorId!,
        creatorMode,
        ctaTemplates: true,
      });
    }
  }

  private saveCreator(): void {
    if (!this.nameForm.valid || this.isCreatingCreator()) {
      return;
    }

    this.updateName(this.creator()!, this.nameForm.value.name!.trim());
  }

  private updateName(creator: ICreator, newName: string, source: 'manual' | 'use_as_default' = 'manual'): void {
    this.isCreatingCreator.set(true);
    this.nameError.set(null);
    this.nameForm.get('name')?.setErrors(null);
    this.nameForm.disable();

    this.creatorStore
      .updateCreatorName(creator.id, { icon: creator.icon, name: newName })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.isCreatingCreator.set(false);
          this.nameForm.enable();

          const nameError = this.creatorStore.nameError$();

          if (nameError) {
            if (this.isEditingName) {
              this.nameError.set(nameError);
              this.nameForm.get('name')?.markAsTouched();
              this.nameForm.get('name')?.setErrors({ error: true });
            } else {
              // The edit form is closed in this flow (e.g. rename via "use as default"), so the
              // inline mat-error is invisible - surface the conflict as a snackbar instead.
              this.snackbarService.openError(nameError);
            }
          }
        },
        next: (updatedCreator) => {
          this.isCreatingCreator.set(false);
          this.nameForm.enable();
          this.isEditingName = false;

          const confirmedCreator = { ...updatedCreator, name: newName };
          this.setCreator(confirmedCreator);
          this.eventTrackingService.track(EEventType.creatorRenamed, {
            creatorId: confirmedCreator.id,
            newValue: newName,
            previousValue: creator.name ?? undefined,
            source,
          });
          this.creatorUpdated.emit(confirmedCreator);
        },
      });
  }

  private setCreator(creator: ICreator | null, emitEvent = true): void {
    this.creator.set(creator);

    if (creator) {
      this.iconForm.patchValue(
        {
          icon: creator.icon,
          imageFile: ImageUtilitiesService.isValidFileId(creator.icon)
            ? FileUploaderService.createCustomFile(creator.icon!)
            : null,
        },
        { emitEvent },
      );
    }

    const banner = this.bannerComponent();

    if (banner) {
      banner.setCreator(creator, emitEvent);
    }
  }

  private trackTabOpened(tab: MatTab | null): void {
    let source: string | null = null;

    if (tab === this.campaignsTab()) {
      source = 'campaigns';
    } else if (tab === this.deliverablesTab()) {
      source = 'deliverables';
    } else if (tab === this.channelsTab()) {
      source = 'channels';
    } else if (tab === this.contentsTab()) {
      source = 'contents';
    }

    if (!source) {
      return;
    }

    this.eventTrackingService.track(EEventType.creatorTabOpened, {
      creatorId: this.creatorId() ?? this.creator()?.id,
      source,
    });
  }

  private clearCreatorName(creatorName: string): string {
    return creatorName.substring(0, 1) === '@' ? creatorName.substring(1) : creatorName;
  }

  private getSocialContents(): void {
    if (this.creatorMode()) {
      return;
    }

    const { endDate, startDate } = untracked(this.creatorDetailsFilter);

    this.store.fetchSocialContents({
      creatorId: this.creatorId() || this.creator()!.id,
      endDate: endDate ?? undefined,
      startDate: startDate ?? undefined,
    });
  }

  private setInitialFilters(): void {
    const persistedDateFilterState = this.dateFilterState();

    if (persistedDateFilterState?.startDate && persistedDateFilterState.endDate) {
      this.creatorDetailsFilter.set({
        channelTechnicalIds: new Set(),
        endDate: endOfDay(persistedDateFilterState.endDate),
        startDate: startOfDay(persistedDateFilterState.startDate),
      });

      return;
    }

    const today = endOfDay(new Date());
    const last30DaysStart = startOfDay(subDays(today, 29));

    this.creatorDetailsFilter.set({
      channelTechnicalIds: new Set(),
      endDate: today,
      startDate: last30DaysStart,
    });
  }

  private initializeDateFilterState(): void {
    const creatorId = this.creatorId();
    const today = endOfDay(new Date());
    const last30DaysStart = startOfDay(subDays(today, 29));
    const resolvedState = this.profileDateFilterStateService.resolveState({
      defaultEndDate: today,
      defaultSelectedRange: EDateRangeType.last30Days,
      defaultStartDate: last30DaysStart,
      entityId: creatorId,
      entityType: 'creator',
      hideAllTimeRangeOption: true,
      userId: AuthService.currentDbUser?.id ?? null,
    });

    this.dateFilterState.set(resolvedState.filterState);
    this.selectedDateRange = resolvedState.selectedRange;
  }

  private initializeDateFilterStateEffect(): void {
    effect(() => {
      const creatorId = this.creatorId();

      if (!creatorId || this.dateFilterStateInitialized) {
        return;
      }

      this.initializeDateFilterState();
      this.setInitialFilters();
      this.dateFilterStateInitialized = true;
    });
  }

  // selectedTabChange doesn't fire for the initially selected tab, which is Deliverables when the
  // Campaigns tab is permission-hidden. MatTab.isActive is only set a microtask after the first
  // render, but `position` is already 0 for the selected tab by then.
  private openDeliverablesTabIfInitiallySelected(): void {
    afterNextRender(() => {
      if (this.deliverablesTab()?.position === 0) {
        this.deliverablesTabOpened.set(true);
      }
    });
  }

  private setCreatorId(): void {
    const urlCreatorId = this.route.snapshot.paramMap.get('id');

    if (urlCreatorId) {
      this.creatorId.set(urlCreatorId);

      return;
    }

    effect(() => {
      const userCreatorId = AuthService.currentDbUser?.creatorId;
      const creatorMode = this.creatorMode();

      if (creatorMode && userCreatorId) {
        this.creatorId.set(userCreatorId);

        return;
      }

      this.router.navigate(['/dashboard']);
    });
  }
}
